import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  BackgroundType,
  ColourPoint,
  GameMode,
  GridBoxPath,
  Point,
  SpecialTile,
  worlds,
  customWorlds,
} from "./types";
import { generatePuzzle } from "./utils/generatePuzzle";
import { playSFX } from "./utils/playSFX";
import { onNewPuzzle } from "./utils/onNewPuzzle";
import TitlePage from "./pages/TitlePage";
import GamePage from "./pages/GamePage";

function App() {
  const [completedPaths, setCompletedPaths] = useState<{
    [key: string]: boolean;
  }>({});
  const [puzzle, setPuzzle] = useState<{
    circles: ColourPoint[];
    size: number;
    wallTiles: Point[];
    specialTiles: SpecialTile[];
    stageEffects: string[];
    colorCount?: number;
    backgroundType?: BackgroundType;
  }>({
    circles: [],
    size: 0,
    colorCount: -1,
    wallTiles: [],
    specialTiles: [],
    backgroundType: BackgroundType.solidBlack,
    stageEffects: [],
  });
  const [size, setSize] = useState(3);
  const [colourCount, setColourCount] = useState(1);
  const [path, setPath] = useState<{ [key: string]: GridBoxPath }>({});
  const [level, setLevel] = useState(1);
  const [levelNumber, setLevelNumber] = useState(1);
  const [worldNumber, setWorldNumber] = useState(1);
  const [worldLevelNumber, setWorldLevelNumber] = useState(1);
  const [numberOfConnectedColors, setNumberOfConnectedColors] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupText, setPopupText] = useState("");
  const [unlockedStageTypes, setUnlockedStageTypes] = useState<string[]>([]);
  const [popupColor, setPopupColor] = useState("rgba(0,0,0,0.7)");
  const [gameMode, setGameMode] = useState<GameMode>(GameMode.standard);
  const [bombTimer, setBombTimer] = useState<number>(0);
  const [levelTimer, setLevelTimer] = useState<number>(0);
  const [selectedWorldId, setSelectedWorldId] = useState<number>(0);
  const [isOutOfTime, setIsOutOfTime] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (showPopup) {
      timeoutId = setTimeout(() => {
        setShowPopup(false);
      }, 1500);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [showPopup]);

  const triggerPopup = (text: string, color: string = "rgba(0,0,0,0.7)") => {
    setPopupText(text);
    setPopupColor(color);
    setShowPopup(true);
  };

  // when all paths are completed, show a modal
  useEffect(() => {
    if (
      Object.values(completedPaths).filter((v) => v).length ===
      puzzle.colorCount
    ) {
      onNewPuzzle({
        setPath,
        setCompletedPaths,
        setNumberOfConnectedColors,
        level,
        size,
        colourCount,
        setLevel,
        setSize,
        setColourCount,
        setPuzzle,
        unlockedStageTypes,
        setUnlockedStageTypes,
        triggerPopup,
        levelNumber,
        setLevelNumber,
        gameMode,
        setGameMode,
        setBombTimer,
        levelTimer,
        setLevelTimer,
        worldNumber,
        setWorldNumber,
        worldLevelNumber,
        setWorldLevelNumber,
        selectedWorldId,
      });
    } else {
      if (
        numberOfConnectedColors <
        Object.values(completedPaths).filter((v) => v).length
      ) {
        const filename = "SFX/connect1.wav";
        const success = new Audio(filename);
        success.volume = 0.3;
        success.play();
      }
      setNumberOfConnectedColors(
        Object.values(completedPaths).filter((v) => v).length
      );
    }
  }, [completedPaths]);

  const beginNewPuzzle = (mode?: GameMode, worldId?: number) => {
    // Determine starting parameters based on game mode
    let startSize = 3;
    let startColors = 1;
    let backgroundType: BackgroundType = BackgroundType.solidBlack;
    let effects: string[] = [];

    if (mode === GameMode.adventure) {
      // Use first world settings for adventure mode
      const world = worlds[0];
      startSize = world.startingSize;
      startColors = world.startingColors;
      backgroundType = world.backgroundType || BackgroundType.solidBlack;
      effects = world.effects;
    } else if (mode === GameMode.worldSelect && worldId !== undefined) {
      // Use selected custom world settings
      const world = customWorlds[worldId];
      if (world) {
        startSize = world.startingSize;
        startColors = world.startingColors;
        backgroundType = world.backgroundType || BackgroundType.solidBlack;
        effects = world.effects;
      }
    }

    const { circles, wallTiles, specialTiles, stageEffects } = generatePuzzle(
      startSize,
      startColors,
      effects
    );

    setLevelNumber(1);
    setLevel(1);
    setWorldLevelNumber(1);
    setWorldNumber(1);
    setSize(startSize);
    setColourCount(startColors);
    setPath({});
    setCompletedPaths({});
    setPuzzle({
      circles,
      size: startSize,
      wallTiles: wallTiles,
      colorCount: startColors,
      backgroundType,
      specialTiles: specialTiles || [],
      stageEffects: stageEffects || [],
    });
    setIsOutOfTime(false);
  };

  // countdown the level timer each second
  useEffect(() => {
    if (levelTimer > 0) {
      const interval = setInterval(() => {
        setLevelTimer((prev) => prev - 1);

        if (levelTimer === 1) {
          playSFX("SFX/boom-big.wav");
          setPath({});
          setCompletedPaths({});
          setPuzzle({
            circles: [],
            size: 0,
            wallTiles: [],
            specialTiles: [],
            stageEffects: [],
            colorCount: -1,
            backgroundType: BackgroundType.solidBlack,
          });
          setIsOutOfTime(true);
          clearInterval(interval);
        }

        if (levelTimer === 0) {
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [levelTimer]);

  const handleOutOfTimeClose = () => {
    setIsOutOfTime(false);
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <TitlePage
              setGameMode={setGameMode}
              setUnlockedStageTypes={setUnlockedStageTypes}
              setLevelTimer={setLevelTimer}
              beginNewPuzzle={beginNewPuzzle}
              selectedWorldId={selectedWorldId}
              setSelectedWorldId={setSelectedWorldId}
            />
          }
        />
        <Route
          path="/game"
          element={
            <GamePage
              puzzle={puzzle}
              setPuzzle={setPuzzle}
              path={path}
              setPath={setPath}
              completedPaths={completedPaths}
              setCompletedPaths={setCompletedPaths}
              levelNumber={levelNumber}
              setLevelNumber={setLevelNumber}
              level={level}
              setLevel={setLevel}
              size={size}
              setSize={setSize}
              colourCount={colourCount}
              setColourCount={setColourCount}
              unlockedStageTypes={unlockedStageTypes}
              setUnlockedStageTypes={setUnlockedStageTypes}
              gameMode={gameMode}
              setGameMode={setGameMode}
              bombTimer={bombTimer}
              setBombTimer={setBombTimer}
              levelTimer={levelTimer}
              setLevelTimer={setLevelTimer}
              worldNumber={worldNumber}
              setWorldNumber={setWorldNumber}
              worldLevelNumber={worldLevelNumber}
              setWorldLevelNumber={setWorldLevelNumber}
              selectedWorldId={selectedWorldId}
              setSelectedWorldId={setSelectedWorldId}
              numberOfConnectedColors={numberOfConnectedColors}
              setNumberOfConnectedColors={setNumberOfConnectedColors}
              showPopup={showPopup}
              popupText={popupText}
              popupColor={popupColor}
              triggerPopup={triggerPopup}
              beginNewPuzzle={beginNewPuzzle}
              isOutOfTime={isOutOfTime}
              onOutOfTimeClose={handleOutOfTimeClose}
            />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
